'use client';
import { SafeSessionStorage } from '#pplx/frontend/browser-storage/sessionStorage.js';
import { usePrevious } from '#pplx/frontend/common/hooks/usePrevious.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { withoutCommandMenuMarker } from '#pplx/frontend/composer-sdk/withoutCommandMenuMarker.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { createZustandStoreTypedReactContext } from '#pplx/frontend/zustand-context/createZustandContext.js';
import { useLayoutEffect } from 'react';
import { create } from 'zustand';
export const ACTIVE_QUERY_KEY = 'pplx.activeQuery';
export const COMPUTER_ACTIVE_QUERY_KEY = 'pplx.activeQuery.computer';
// Document-level event to update the ask input from outside its
// store scope. The sidecar and thread follow-up inputs are isolated from this
// global store, so callers that need to reach them (e.g. the control-browser
// upsell CTA, the xplat focus-prompt shortcut) dispatch this event; surfaces
// with isolated input state listen and apply it. Omitting `value` leaves the
// current draft untouched (focus-only dispatch).
export const ASK_INPUT_SET_TEXT_EVENT = 'pplx:ask-input-set-text';
const PENDING_ASK_INPUT_PREFILL_KEY = 'pplx.pendingAskInputPrefill';
export const ASK_INPUT_PREFILL_PENDING_EVENT = 'pplx:ask-input-prefill-pending';
export const ASK_INPUT_PREFILL_LANDING_PATHS = ['/', '/computer/new', '/computer'];
export const COMPUTER_ASK_INPUT_PATH = '/computer';
// Session storage can be unavailable in hardened or quota-constrained browser
// contexts. Keep the same-document navigation path lossless in that case.
let inMemoryPendingAskInputPrefill;
/**
 * Stages a composer draft for the landing page to consume after navigation.
 * Session storage keeps the handoff intact if routing requires a full reload;
 * the event makes the same-page case immediate.
 */
export function setPendingAskInputPrefill(prefill) {
    inMemoryPendingAskInputPrefill = prefill;
    SafeSessionStorage.setItem(PENDING_ASK_INPUT_PREFILL_KEY, JSON.stringify(prefill));
    if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event(ASK_INPUT_PREFILL_PENDING_EVENT));
    }
}
export function getPendingAskInputPrefill() {
    if (inMemoryPendingAskInputPrefill) {
        return inMemoryPendingAskInputPrefill;
    }
    const serializedPrefill = SafeSessionStorage.getItem(PENDING_ASK_INPUT_PREFILL_KEY);
    if (!serializedPrefill) {
        return undefined;
    }
    try {
        const prefill = JSON.parse(serializedPrefill);
        // TODO(thomas.wang): Temporary. Remove once no open tab can hold a `sourceMentions` draft.
        return Array.isArray(prefill.mentions) ? prefill : undefined;
    }
    catch {
        return undefined;
    }
}
export function takePendingAskInputPrefill() {
    const prefill = getPendingAskInputPrefill();
    // Both backends mirror the same one-shot handoff, so consume them together.
    inMemoryPendingAskInputPrefill = undefined;
    SafeSessionStorage.removeItem(PENDING_ASK_INPUT_PREFILL_KEY);
    return prefill;
}
let pendingAskInputSearchMode;
export const ASK_INPUT_SEARCH_MODE_PENDING_EVENT = 'pplx:ask-input-search-mode-pending';
export function setPendingAskInputSearchMode(searchMode) {
    pendingAskInputSearchMode = searchMode;
    document.dispatchEvent(new Event(ASK_INPUT_SEARCH_MODE_PENDING_EVENT));
}
export function takePendingAskInputSearchMode() {
    const searchMode = pendingAskInputSearchMode;
    pendingAskInputSearchMode = undefined;
    return searchMode;
}
export function dispatchAskInputSetText(detail) {
    if (typeof document === 'undefined')
        return;
    document.dispatchEvent(new CustomEvent(ASK_INPUT_SET_TEXT_EVENT, {
        detail,
    }));
}
const COMPUTER_TASK_PATH_PREFIX = '/computer/tasks/';
function getLastPathSegment(pathname) {
    return pathname.split('/').filter(Boolean).at(-1);
}
export function shouldResetQueryInputOnNavigation(previousPathname, pathname) {
    if (!previousPathname || !pathname || previousPathname === pathname) {
        return false;
    }
    if ((previousPathname === '/' && pathname.startsWith('/search/new')) ||
        (previousPathname.startsWith('/search/new') && pathname.startsWith('/search')) ||
        (previousPathname.startsWith('/search/new') && pathname === '/')) {
        return false;
    }
    if (previousPathname.startsWith(COMPUTER_TASK_PATH_PREFIX) &&
        pathname.startsWith(COMPUTER_TASK_PATH_PREFIX) &&
        validateUuid(getLastPathSegment(previousPathname))) {
        return false;
    }
    return true;
}
function hydrateActiveQuery(storageKey = ACTIVE_QUERY_KEY) {
    const activeQuery = SafeSessionStorage.getItem(storageKey);
    if (!activeQuery) {
        return {};
    }
    try {
        const sessionData = JSON.parse(activeQuery);
        return sessionData;
    }
    catch {
        return {};
    }
}
function dehydrateActiveQuery(activeQuery, storageKey = ACTIVE_QUERY_KEY) {
    // Keep the open menu in the controlled value while saving only durable draft content.
    const persistedQuery = activeQuery.rawQueryJson === undefined
        ? activeQuery
        : { ...activeQuery, rawQueryJson: withoutCommandMenuMarker(activeQuery.rawQueryJson) };
    SafeSessionStorage.setItem(storageKey, JSON.stringify(persistedQuery));
}
export function clearActiveQuery(storageKey = ACTIVE_QUERY_KEY) {
    SafeSessionStorage.removeItem(storageKey);
}
export function createInputStore(storageKey) {
    return create((set, get) => ({
        userInput: '',
        userInputJson: undefined,
        hasStartedTyping: false,
        shouldTriggerFocus: false,
        actions: {
            restoreInput: () => {
                const activeQuery = hydrateActiveQuery(storageKey);
                if (activeQuery.rawQuery) {
                    set({
                        userInput: activeQuery.rawQuery,
                        userInputJson: activeQuery.rawQueryJson,
                        askDeepResearchSelection: activeQuery.askDeepResearchSelection,
                    });
                }
            },
            setUserInput: (value, json) => {
                const askDeepResearchSelection = value ? get().askDeepResearchSelection : undefined;
                set({
                    userInput: value,
                    userInputJson: json,
                    askDeepResearchSelection,
                });
                const sessionData = {
                    rawQuery: value,
                    rawQueryJson: json,
                    askDeepResearchSelection,
                };
                dehydrateActiveQuery(sessionData, storageKey);
            },
            setAskDeepResearchDraftSelection: (askDeepResearchSelection) => {
                set({ askDeepResearchSelection });
                const { userInput, userInputJson } = get();
                dehydrateActiveQuery({ rawQuery: userInput, rawQueryJson: userInputJson, askDeepResearchSelection }, storageKey);
            },
            setHasStartedTyping: (value) => set({ hasStartedTyping: value }),
            setStartTypingTime: (time) => set({ startTypingTime: time }),
            resetInput: () => {
                set({
                    userInput: '',
                    userInputJson: undefined,
                    askDeepResearchSelection: undefined,
                    hoverPlaceholder: undefined,
                });
                clearActiveQuery(storageKey);
            },
            markInputAsSubmitted: () => {
                set({
                    userInput: '',
                    userInputJson: undefined,
                    askDeepResearchSelection: undefined,
                    startTypingTime: undefined,
                    hoverPlaceholder: undefined,
                });
            },
            setShouldTriggerFocus: (value) => {
                set({
                    shouldTriggerFocus: value,
                });
            },
            setHoverPlaceholder: (value) => {
                set({ hoverPlaceholder: value });
            },
        },
    }));
}
const queryInputStateContext = createZustandStoreTypedReactContext('QueryInputStateContext');
export const queryInputStateContextProvider = queryInputStateContext.Context;
const useSelector = queryInputStateContext.useSelector;
export function useQueryInputSideEffects() {
    const { restoreInput, resetInput } = useInputActions();
    const pathname = usePathname();
    const previousPathname = usePrevious(pathname);
    // Hydrate the active query when the component mounts
    // Note, we technically can do this when zustand is initialized, but
    // that'll cause a hydration error
    useLayoutEffect(() => {
        restoreInput();
    }, [restoreInput]);
    // Reset the input when the user navigates to a new page
    useLayoutEffect(() => {
        if (!shouldResetQueryInputOnNavigation(previousPathname, pathname)) {
            return;
        }
        // It's ok to reset going to `/search` bc at that point we already have the backend uuid
        resetInput();
    }, [pathname, previousPathname, resetInput]);
}
export const useUserInput = () => useSelector((state) => state.userInput);
export const useUserInputJson = () => useSelector((state) => state.userInputJson);
export const useAskDeepResearchDraftSelection = () => useSelector((state) => state.askDeepResearchSelection);
export const useHasStartedTyping = () => useSelector((state) => state.hasStartedTyping);
export const useStartTypingTime = () => useSelector((state) => state.startTypingTime);
export const useShouldTriggerFocus = () => useSelector((state) => state.shouldTriggerFocus);
export const useHoverPlaceholder = () => useSelector((state) => state.hoverPlaceholder);
export const useInputActions = () => useSelector((state) => state.actions);
//# sourceMappingURL=queryInput.js.map